import { FC, useEffect, useRef, useState } from "react"
import JSONEditor, { JSONEditorOptions } from "jsoneditor"
import "jsoneditor/dist/jsoneditor.min.css"

export type JSONEditorSelection = {
  path: (string | number)[]
  value: any
} | null

type ReactJSONEditorProps = {
  json: any
  minHeight?: number
  onSelectionChange?: (selection: JSONEditorSelection) => void
} & Pick<
  JSONEditorOptions,
  | "name"
  | "mode"
  | "onChange"
  | "onChangeJSON"
  | "onChangeText"
  | "search"
  | "enableSort"
>

const ReactJSONEditor: FC<ReactJSONEditorProps> = ({
  json,
  minHeight = 400,
  name = "JSON editor",
  mode = "tree",
  search = true,
  enableSort = false,
  onChange,
  onChangeJSON,
  onChangeText,
  onSelectionChange
}) => {
  const [editor, setEditor] = useState<JSONEditor | null>(null)
  const editorRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const editorOptions: JSONEditorOptions = {
      mode,
      name,
      enableTransform: false,
      search,
      enableSort,
      onChange,
      onChangeJSON,
      onChangeText,
      onEvent: (node, event) => {
        if (onSelectionChange && event.type === "click" && node) {
          const value = node.value !== undefined ? node.value : node.field
          onSelectionChange({
            path: node.path || [],
            value: value
          })
        }
      }
    }

    if (editorRef.current) {
      const jsonEditor = new JSONEditor(editorRef.current, editorOptions)
      jsonEditor.set(json)
      setEditor(jsonEditor)
    }

    return () => {
      if (!!editor) {
        editor.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (editor) {
      editor.setMode(mode)
    }
  }, [editor, mode])

  useEffect(() => {
    if (editor && json) {
      try {
        const current = editor.get()
        if (JSON.stringify(current) !== JSON.stringify(json)) {
          editor.update(json)
        }
      } catch {
        editor.set(json)
      }
    }
  }, [json, editor])

  return (
    <div
      className="ReactJSONEditor"
      ref={editorRef}
      style={{ height: "100%", minHeight }}
    />
  )
}

export default ReactJSONEditor
